import { createContext, FocusEvent, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import useSetFocusById from '../hooks/useSetFocus'

export const navigationControls = ['Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']
export const activationControls = ['Enter', ' ']

export const kbControlsLayerDefault = 'default'

type FocusEnterFn = (() => void) | null

interface FocusableElement {
  id: string
  layer: string
}

interface KeyboardControlsContextValue {
  isEnabled: boolean
  activeLayer: string
  getFocusedElement: () => string | null
  setFocusedElement: (elementId: string, layer?: string) => void
  isElementFocused: (elementId: string) => boolean
  focusNextElement: () => void
  focusPreviousElement: () => void
  handleNonKeyboardFocus: (event: FocusEvent) => void

  addFocusableElement: (elementId: string, layer: string, isExternal: boolean, manualFocusHandling: boolean, onFocusEnterFromStart: FocusEnterFn, onFocusEnterFromEnd: FocusEnterFn) => void
  removeFocusableElement: (elementId: string, layer?: string) => void

  // TODO(Jesus): in the future, replace implementation with a layer stack
  pushActiveLayer: (layer: string) => void
  resetDefaultLayer: () => void
}

const initialContextValue: KeyboardControlsContextValue = {
  isEnabled: false,
  activeLayer: kbControlsLayerDefault,
  getFocusedElement: () => null,
  setFocusedElement: () => {},
  isElementFocused: () => false,
  focusNextElement: () => {},
  focusPreviousElement: () => {},
  handleNonKeyboardFocus: () => {},
  addFocusableElement: () => {},
  removeFocusableElement: () => {},
  pushActiveLayer: () => {},
  resetDefaultLayer: () => {}
}

export const KeyboardControlsContext = createContext<KeyboardControlsContextValue>(initialContextValue)

export const KeyboardControlsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const focusableElements = useRef<FocusableElement[]>([])
  const externalElements = useRef<FocusableElement[]>([])
  const manualFocusElements = useRef<FocusableElement[]>([])
  const focusEnterFromStartFunctions = useRef<FocusEnterFn[]>([])
  const focusEnterFromEndFunctions = useRef<FocusEnterFn[]>([])
  const [isEnabled, setIsEnabled] = useState<boolean>(false)
  const focused = useRef<string>('')
  const [activeLayer, setActiveLayer] = useState<string>(kbControlsLayerDefault)

  const [setFocusById] = useSetFocusById()

  /**
   * Adds an element ID to the list of focusable elements managed by the context.
   *
   * @param {string} elementId - The ID of the element to add to the focusable elements list.
   * @param {string} layer - The layer of the element to add. This layer can be set as the currently active one with the `setActiveLayer` function.
   * @param {boolean} [isExternal=false] - Set to `true` if the element is managed externally (e.g., if it’s an iframe or Unity component).
   * @param {boolean} [manualFocusHandling=false] - Required for external elements like the Unity canvas that does not allow to easily prevent default event actions.
   */
  const addFocusableElement = (
    elementId: string,
    layer: string = kbControlsLayerDefault,
    isExternal: boolean = false,
    manualFocusHandling: boolean = false,
    onFocusEnterFromStart: FocusEnterFn = null,
    onFocusEnterFromEnd: FocusEnterFn = null
  ): void => {
    const focusable = {
      id: elementId,
      layer
    }

    focusableElements.current.push(focusable)
    focusEnterFromStartFunctions.current.push(onFocusEnterFromStart)
    focusEnterFromEndFunctions.current.push(onFocusEnterFromEnd)

    if (isExternal) {
      externalElements.current.push(focusable)
    }

    if (manualFocusHandling) {
      manualFocusElements.current.push(focusable)
    }
  }

  const removeFocusableElement = (elementId: string, layer: string = kbControlsLayerDefault): void => {
    const focusable = {
      id: elementId,
      layer
    }

    let idIndex = focusableElements.current.findIndex(x => x.id === focusable.id && x.layer === focusable.layer)
    if (idIndex !== -1) {
      focusableElements.current.splice(idIndex, 1)
      focusEnterFromStartFunctions.current.splice(idIndex, 1)
      focusEnterFromEndFunctions.current.splice(idIndex, 1)
    }

    idIndex = externalElements.current.findIndex(x => x.id === focusable.id && x.layer === focusable.layer)
    if (idIndex !== -1) {
      externalElements.current.splice(idIndex, 1)
    }

    idIndex = manualFocusElements.current.findIndex(x => x.id === focusable.id && x.layer === focusable.layer)
    if (idIndex !== -1) {
      manualFocusElements.current.splice(idIndex, 1)
    }
  }

  const findFirstFocusableChild = useCallback((elementId: string, layer: string = kbControlsLayerDefault): string | null => {
    const parentElement = document.getElementById(elementId)

    if (parentElement == null) {
      return null
    }

    const searchInChildren = (currentElement: HTMLElement): string | null => {
      for (let i = 0; i < currentElement.children.length; i++) {
        const child = currentElement.children[i] as HTMLElement

        const focusable = {
          id: child.id,
          layer
        }

        if (child.id && focusableElements.current
          .some(x => x.id === focusable.id && x.layer === focusable.layer)) {
          return child.id
        }

        const foundInGrandchildren = searchInChildren(child)
        if (foundInGrandchildren) {
          return foundInGrandchildren
        }
      }
      return null
    }

    return searchInChildren(parentElement)
  }, [])

  const focusFirstFocusableChild = useCallback((elementId: string, layer: string = kbControlsLayerDefault): void => {
    const focusable = {
      id: elementId,
      layer
    }

    const isFocusable = focusableElements.current.some(x => x.id === focusable.id && x.layer === focusable.layer)
    if (!isFocusable) {
      const focusableChild = findFirstFocusableChild(elementId, layer)
      if (focusableChild !== null) {
        focused.current = focusableChild
        setFocusById(focusableChild)
      }
    }
  }, [focusableElements, setFocusById, findFirstFocusableChild])

  const setFocusedElement = useCallback((elementId: string, layer: string = kbControlsLayerDefault): void => {
    const focusable = {
      id: elementId,
      layer
    }

    const isFocusable = focusableElements.current.some(x => x.id === focusable.id && x.layer === focusable.layer)
    if (isFocusable) {
      focused.current = elementId
      setFocusById(elementId)
    } else {
      focusFirstFocusableChild(elementId, layer)
    }
  }, [setFocusById, focusFirstFocusableChild])

  const getFocusedElement = (): string | null => {
    return focused.current
  }

  const isElementFocused = (elementId: string): boolean => {
    return elementId === focused.current
  }

  const rotateFocus = useCallback((delta: number) => {
    const currentFocusable = {
      id: focused?.current,
      layer: activeLayer
    }

    const filteredFocusableElements = focusableElements.current
      .filter((x: FocusableElement) => x.layer === currentFocusable.layer)
    const elementsCount = filteredFocusableElements.length
    let index = filteredFocusableElements
      .findIndex(x => x.id === currentFocusable.id && x.layer === currentFocusable.layer)
    if (index < 0) { index = 0 }

    index += delta
    // Get around TS negative modulo definition to wrap around components
    index = ((index % elementsCount) + elementsCount) % elementsCount

    const newFocusable = filteredFocusableElements[index]

    // Index from the original full `focusableElements` array
    let fullListIndex = focusableElements.current
      .findIndex((x: FocusableElement) => x.id === newFocusable.id && x.layer === newFocusable.layer)
    if (fullListIndex < 0) { fullListIndex = 0 }

    let onFocusEnter = null
    if (delta > 0) {
      onFocusEnter = focusEnterFromStartFunctions.current[fullListIndex]
    } else if (delta < 0) {
      onFocusEnter = focusEnterFromEndFunctions.current[fullListIndex]
    }
    if (onFocusEnter !== null) {
      onFocusEnter()
    }

    const newFocused = filteredFocusableElements[index]
    setFocusedElement(newFocused.id, newFocused.layer)
  }, [setFocusedElement, activeLayer])

  const focusNextElement = useCallback(() => {
    rotateFocus(1)
  }, [rotateFocus])

  const focusPreviousElement = useCallback(() => {
    rotateFocus(-1)
  }, [rotateFocus])

  // Sets up keyboard event handling
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent): void => {
      const currentFocusable = {
        id: focused?.current,
        layer: activeLayer
      }

      const isExternalElement = externalElements.current
        .some(x => x.id === currentFocusable.id && x.layer === currentFocusable.layer)
      const manualFocusHandling = manualFocusElements.current
        .some(x => x.id === currentFocusable.id && x.layer === currentFocusable.layer)

      const keyPressed = event.key
      if (!navigationControls.includes(keyPressed) && !activationControls.includes(keyPressed)) {
        return
      }
      setIsEnabled(true)

      // Handle Enter and Space for focused buttons
      if (keyPressed === 'Enter' || keyPressed === ' ') {
        const isExternalElement = externalElements.current
          .some(x => x.id === currentFocusable.id && x.layer === currentFocusable.layer)
        const manualFocusHandling = manualFocusElements.current
          .some(x => x.id === currentFocusable.id && x.layer === currentFocusable.layer)
        if (!isExternalElement && !manualFocusHandling) {
          event.preventDefault()
          const element = document.getElementById(focused.current)
          if (element != null) {
            element.click()
          }
        }
      }

      // Completely control TAB and SHIFT-TAB key presses to rotate focus
      if (keyPressed === 'Tab') {
        if (manualFocusHandling) {
          event.preventDefault()
        } else
          if (!isExternalElement) {
            if (event.shiftKey) {
              focusPreviousElement()
            } else {
              focusNextElement()
            }

            event.preventDefault()
          }
      }
    }

    document.addEventListener('keydown', handleKeyPress, true)

    return () => {
      document.removeEventListener('keydown', handleKeyPress, true)
    }
  }, [setFocusedElement, focusNextElement, focusPreviousElement, activeLayer])

  const handleNonKeyboardFocus = useCallback((event: FocusEvent): void => {
    const id = (event.target as Element).id

    const focusable = {
      id,
      layer: activeLayer
    }

    if (focusableElements.current.some(x => x.id === focusable.id && x.layer === focusable.layer)) {
      focused.current = focusable.id
      setFocusById(focusable.id)
    } else {
      focusFirstFocusableChild(focusable.id, focusable.layer)
    }
  }, [focusableElements, focusFirstFocusableChild, setFocusById, activeLayer])

  /**
   * Resets the active keyboard controls layer to the default layer.
   * Call this when exiting a modal or overlay to restore normal keyboard navigation.
   */
  const resetDefaultLayer = useCallback(() => {
    setActiveLayer(kbControlsLayerDefault)
  }, [])

  /**
   * Sets the active keyboard controls layer.
   * Use this when entering a modal or overlay that has its own set of focusable elements.
   * Elements registered with the matching layer will be the only ones receiving keyboard navigation.
   *
   * @param layer - The layer name to activate (e.g., 'survey', 'student-menu')
   */
  const pushActiveLayer = useCallback((layer: string) => {
    setActiveLayer(layer)
  }, [])

  return (
    <KeyboardControlsContext.Provider
      value={{
        isEnabled,
        activeLayer,
        getFocusedElement,
        setFocusedElement,
        isElementFocused,
        focusNextElement,
        focusPreviousElement,
        handleNonKeyboardFocus,
        addFocusableElement,
        removeFocusableElement,
        pushActiveLayer,
        resetDefaultLayer
      }}
    >
      {children}
    </KeyboardControlsContext.Provider>
  )
}

export const useKeyboardControls = (): KeyboardControlsContextValue => {
  return useContext(KeyboardControlsContext)
}
